import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
/** Maximum number of visible items before scrolling */
const MAX_VISIBLE_ITEMS = 8;
/**
 * Autocomplete dropdown for @mentions of agent profiles.
 * Renders above the chat input with keyboard navigation and touch-friendly rows.
 *
 * Exposes a `handleKeyDown` method via ref so the parent textarea can delegate
 * navigation keys (ArrowUp/Down, Enter, Escape, Tab) to this component.
 */
export const MentionPalette = forwardRef(function MentionPalette({ profiles, filter, onSelect, onDismiss, visible }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef(null);
    const filtered = profiles.filter((p) => p.name.toLowerCase().startsWith(filter.toLowerCase()));
    // Reset selection when filter or visibility changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [filter, visible]);
    // Scroll selected item into view
    useEffect(() => {
        if (!listRef.current)
            return;
        const items = listRef.current.children;
        const selected = items[selectedIndex];
        if (selected && typeof selected.scrollIntoView === 'function') {
            selected.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedIndex]);
    const selectCurrentProfile = useCallback(() => {
        const profile = filtered[selectedIndex];
        if (profile)
            onSelect(profile);
    }, [filtered, selectedIndex, onSelect]);
    const moveSelection = useCallback((direction) => {
        setSelectedIndex((prev) => {
            const lastIndex = filtered.length - 1;
            if (direction < 0)
                return prev <= 0 ? lastIndex : prev - 1;
            return prev >= lastIndex ? 0 : prev + 1;
        });
    }, [filtered.length]);
    const handleKeyDown = useCallback((e) => {
        if (!visible || filtered.length === 0)
            return false;
        const handlers = {
            ArrowUp: () => moveSelection(-1),
            ArrowDown: () => moveSelection(1),
            Enter: selectCurrentProfile,
            Tab: selectCurrentProfile,
            Escape: onDismiss,
        };
        const handler = handlers[e.key];
        if (!handler)
            return false;
        e.preventDefault();
        handler();
        return true;
    }, [visible, filtered.length, moveSelection, selectCurrentProfile, onDismiss]);
    const handleOptionKeyDown = useCallback((e, profile) => {
        if (e.key !== 'Enter' && e.key !== ' ')
            return;
        e.preventDefault();
        onSelect(profile);
    }, [onSelect]);
    const activeDescendantId = filtered[selectedIndex]
        ? `mention-profile-${filtered[selectedIndex].id}`
        : undefined;
    useImperativeHandle(ref, () => ({ handleKeyDown, activeDescendantId }), [
        handleKeyDown,
        activeDescendantId,
    ]);
    if (!visible || filtered.length === 0)
        return null;
    const maxHeight = MAX_VISIBLE_ITEMS * 44;
    return (_jsx("div", { className: "mb-2", children: _jsx("div", { className: "rounded-lg shadow-lg", style: {
                backgroundColor: 'var(--sam-color-bg-surface)',
                border: '1px solid var(--sam-color-border-default)',
            }, children: _jsx("ul", { ref: listRef, id: "mention-palette-listbox", role: "listbox", "aria-label": "Agent profiles", className: "overflow-y-auto", style: { maxHeight }, children: filtered.map((profile, idx) => (_jsx("li", { id: `mention-profile-${profile.id}`, role: "option", "aria-selected": idx === selectedIndex, className: "flex items-start gap-2 px-3 py-2 cursor-pointer select-none", style: {
                        minHeight: 44,
                        backgroundColor: idx === selectedIndex
                            ? 'var(--sam-color-bg-surface-hover)'
                            : 'transparent',
                    }, onClick: () => onSelect(profile), onKeyDown: (e) => handleOptionKeyDown(e, profile), onMouseEnter: () => setSelectedIndex(idx), tabIndex: -1, children: _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "flex items-center gap-2 flex-wrap", children: _jsxs("span", { className: "font-mono text-sm font-medium shrink-0", style: { color: 'var(--sam-color-fg-primary)' }, children: ["@", profile.name] }) }), profile.description && (_jsx("span", { className: "text-sm leading-snug", style: { color: 'var(--sam-color-fg-muted)' }, children: profile.description }))] }) }, profile.id))) }) }) }));
});
//# sourceMappingURL=MentionPalette.js.map